/**
 * Раскладка списка заметок по личным секциям (общая для сайта и приложения).
 */

import { NoteSort } from "@taskflow/contracts";

import type { NoteListItem, NoteListPage, NoteSection } from "./api";

// ─── Раскладка ────────────────────────────────────────────────────────────

export interface GroupedNotes {
  sections: { section: NoteSection; items: NoteListItem[] }[];
  own: NoteListItem[];
  shared: NoteListItem[];
}

/** Порядок внутри секции — тот же, что сервер даёт в общих блоках. */
function compare(sort: NoteSort): (a: NoteListItem, b: NoteListItem) => number {
  return (a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    if (sort === NoteSort.TITLE) {
      if (!a.title || !b.title) return a.title ? -1 : b.title ? 1 : 0;
      return a.title.localeCompare(b.title, "ru", { sensitivity: "base" });
    }
    const key = sort === NoteSort.CREATED ? "createdAt" : "editedAt";
    return b[key].localeCompare(a[key]);
  };
}

/**
 * Порядок в секции: сначала расставленные вручную (по месту), затем
 * остальные — по выбранной сортировке. Ручной порядок главнее и
 * закрепления: его человек задал прямо для этой секции.
 */
function compareInSection(sort: NoteSort): (a: NoteListItem, b: NoteListItem) => number {
  const fallback = compare(sort);
  return (a, b) => {
    if (a.sectionPosition !== null && b.sectionPosition !== null) return a.sectionPosition - b.sectionPosition;
    if (a.sectionPosition !== null || b.sectionPosition !== null) return a.sectionPosition !== null ? -1 : 1;
    return fallback(a, b);
  };
}

/**
 * Заметки по секциям и общим блокам. В секции — и свои, и чужие
 * заметки вперемешку, поэтому порядок в ней наводится заново.
 */
export function groupNotes(page: NoteListPage, sort: NoteSort): GroupedNotes {
  const known = new Set(page.sections.map((section) => section.id));
  const inSection = (item: NoteListItem) => item.sectionId !== null && known.has(item.sectionId);
  const all = [...page.own, ...page.shared];
  return {
    sections: page.sections.map((section) => ({
      section,
      items: all.filter((item) => item.sectionId === section.id).sort(compareInSection(sort)),
    })),
    own: page.own.filter((item) => !inSection(item)),
    shared: page.shared.filter((item) => !inSection(item)),
  };
}

